import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import Layout from "../components/Layout";
import { useApiStore } from "../store";
import useI18n from "../hooks/useI18n";

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  const { showModal, forgotPassword, confirmForgotPassword } = useApiStore(
    (state) => state
  );

  const handleRequestReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const user = data.get("username")?.toString() || "";
    const mail = data.get("email")?.toString() || "";
    setUsername(user);
    setEmail(mail);

    const success = await forgotPassword(user, mail);
    if (success) {
      setStep(2);
    }
  };

  const handleConfirmReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const code = data.get("code")?.toString() || "";
    const newPassword = data.get("newPassword")?.toString() || "";
    const confirmPassword = data.get("confirmPassword")?.toString() || "";

    if (newPassword !== confirmPassword) {
      showModal(t("form.data_validation"), t("form.passwords_do_not_match"));
      return;
    }

    const success = await confirmForgotPassword(username, code, newPassword);
    if (success) {
      showModal(t("form.success"), t("auth.password_reset_success"), t("errors.ok"), undefined, "SignIn");
      navigate("/signin");
    }
  };

  return (
    <Layout>
      <Container component="main" sx={{ maxWidth: { xs: "sm", md: "sm" } }}>
        <Paper
          sx={{
            marginTop: 8,
            padding: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOpenIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t("auth.forgot_password")}
          </Typography>

          {step === 1 ? (
            <Box
              component="form"
              onSubmit={handleRequestReset}
              noValidate
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label={t("auth.username")}
                name="username"
                autoComplete="username"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t("auth.email")}
                name="email"
                autoComplete="email"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                size="large"
              >
                {t("auth.request_reset_code")}
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleConfirmReset}
              noValidate
              sx={{ mt: 1, width: "100%" }}
            >
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("auth.reset_code_sent_to_email")}
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label={t("auth.confirmation_code")}
                name="code"
                autoComplete="off"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label={t("auth.new_password")}
                type="password"
                id="newPassword"
                autoComplete="new-password"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t("auth.confirm_password")}
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                size="large"
              >
                {t("auth.reset_password")}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Link href="#" onClick={() => navigate("/signin")} variant="body2">
              {t("auth.back_to_signin")}
            </Link>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
};

export default ForgotPasswordScreen;
